import React from "react";
import "./StaggeredMenu.css";

// --- STAGGERED MENU (adapted from React Bits "StaggeredMenu-JS-CSS") ---
// A self-contained toggle button + full slide-in panel. Opening plays a
// staggered wipe: each colour in `colors` sweeps in from `position` in
// sequence (earliest colour first), and the panel itself is the LAST,
// topmost layer in that same sequence — solid white, arriving after every
// colour has swept through, so it ends up covering them completely. The
// resting state is therefore always a plain white panel; the colours are
// purely a transient opening flourish. Menu items stagger in on top of
// that white panel with their own increasing delay — hence "staggered".
//
// NOTE ON POSITIONING: every piece (toggle button, backdrop, colour
// layers, panel) uses `position: absolute`, not `fixed`. Mount this
// inside a `position: relative` wrapper sized to whatever area it should
// cover — e.g. this app's screens are each a centered max-w-md "card"
// rather than the raw viewport, so `fixed` would break out of that card
// on wider screens.
//
// Colour theming: pass `colors` (typically just the active category's
// single colour — one sweep, then white) and `accentColor` (usually the
// same colour). Items render in black by default; `accentColor` only
// shows up once an item is actually selected (`isActive`) or hovered —
// matching "white page, black text, colour only on click".

export interface StaggeredMenuItem {
  label: string;
  ariaLabel?: string;
  link?: string;
  onClick?: () => void;
  isActive?: boolean;
}

// The hamburger↔X morph icon, exported standalone so it can be dropped
// into an existing header button (like this app's own Menu trigger)
// instead of only living inside StaggeredMenu's built-in toggle button.
// Colour comes from `currentColor`, so wrap it in an element with a
// `color` style/class the same way you would a lucide icon.
export function StaggeredMenuBurgerIcon({
  isOpen,
  size = 24,
}: {
  isOpen: boolean;
  size?: number;
}) {
  return (
    <span
      className={`staggered-menu-burger staggered-menu-burger-standalone${
        isOpen ? " is-open" : ""
      }`}
      style={{ width: size * 0.92, height: size * 0.67 }}
      aria-hidden="true"
    >
      <span />
      <span />
      <span />
    </span>
  );
}

export interface StaggeredMenuProps {
  position?: "left" | "right";
  items: StaggeredMenuItem[];
  socialItems?: StaggeredMenuItem[];
  displaySocials?: boolean;
  displayItemNumbering?: boolean;
  menuButtonColor?: string;
  openMenuButtonColor?: string;
  changeMenuColorOnOpen?: boolean;
  /** Colour(s) that sweep in before the white panel settles on top. Usually just [categoryColor]. */
  colors?: string[];
  logoUrl?: string;
  /** Drives numbering, hover state, the active-item colour, and the close button's hover colour. */
  accentColor?: string;
  /** Optional heading shown at the top of the panel (e.g. the category name). */
  panelTitle?: string;
  /** Optional label shown on the toggle button itself, next to the burger icon. */
  toggleLabel?: string;
  /** Text next to the close icon inside the panel. Set to "" to show only the icon. */
  closeLabel?: string;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  /** Pass these two together to drive the menu from a parent's own state (e.g. an existing header button). Omit both to let the menu manage itself with its own toggle button. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the component's own toggle button — use when a parent button already opens it via `open`. */
  hideToggleButton?: boolean;
}

export default function StaggeredMenu({
  position = "right",
  items,
  socialItems = [],
  displaySocials = false,
  displayItemNumbering = true,
  menuButtonColor = "#1c0d02",
  openMenuButtonColor = "#fff",
  changeMenuColorOnOpen = true,
  colors = ["#5227FF"],
  logoUrl,
  accentColor = "#5227FF",
  panelTitle,
  toggleLabel,
  closeLabel = "Close",
  onMenuOpen,
  onMenuClose,
  open,
  onOpenChange,
  hideToggleButton = false,
}: StaggeredMenuProps) {
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isOpen = isControlled ? !!open : internalOpen;

  // A brief "pressed" state on the close button so clicking it has its
  // own visible feedback (icon spins + shrinks) before the panel's own
  // slide-out transition takes over.
  const [isCloseButtonPressed, setIsCloseButtonPressed] = React.useState(false);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const setOpen = (val: boolean) => {
    if (!isControlled) setInternalOpen(val);
    onOpenChange?.(val);
  };

  // Skip firing onMenuOpen/onMenuClose on the very first render — those
  // callbacks should only fire on actual open/close transitions.
  const hasMountedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (isOpen) onMenuOpen?.();
    else onMenuClose?.();
  }, [isOpen]);

  const handleItemActivate = (item: StaggeredMenuItem) => {
    // Previously this fired `item.onClick` (which navigates — e.g. to the
    // subcategory transition screen) BEFORE closing the panel. Since that
    // navigation usually swaps `currentScreen` and unmounts this whole
    // component immediately, the close transition never got a chance to
    // render at all — the panel just vanished with the new screen already
    // in its place. Reversing the order lets the panel actually play its
    // reverse sweep (panel retreats first, closely followed by the colour
    // layer — the mirror of how it opened) while still mounted, and only
    // swaps screens once that's had time to finish.
    setOpen(false);
    window.setTimeout(() => {
      item.onClick?.();
    }, MENU_CLOSE_ANIMATION_MS);
  };

  const handleCloseButtonClick = () => {
    setIsCloseButtonPressed(true);
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      setIsCloseButtonPressed(false);
    }, 180);
  };

  const toggleColor =
    changeMenuColorOnOpen && isOpen ? openMenuButtonColor : menuButtonColor;

  // The white panel is treated as one more entry at the END of the sweep
  // sequence — it gets the largest transition-delay of all, so it always
  // arrives last and covers every colour beneath it.
  const panelDelayMs = colors.length * 70 + 90;

  // How long the CLOSING sequence takes to visually finish (panel retreats
  // immediately, colour layer(s) follow with a `(colors.length - i) * 40`ms
  // stagger, each then playing its own CSS transition). `handleItemActivate`
  // waits this long before actually navigating, so the reverse animation is
  // seen rather than skipped. Now that StaggeredMenu.css is available, this
  // is read off its actual numbers instead of estimated: the panel closes
  // with 0 delay and the colour layer with the LARGEST close-delay is the
  // first one (`i = 0`), which gets `colors.length * 40`ms — both then play
  // `.staggered-menu-panel`/`.staggered-menu-layer`'s own
  // `transition: transform 0.5s ...` (i.e. 500ms). So the whole reverse
  // sweep is fully finished at `colors.length * 40 + 500`ms after close;
  // +20ms of padding avoids a hair-trigger cutoff.
  const MENU_CLOSE_ANIMATION_MS = colors.length * 40 + 500 + 20;

  return (
    <>
      {!hideToggleButton && (
        <button
          type="button"
          onClick={() => setOpen(!isOpen)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
          className={`staggered-menu-toggle staggered-menu-toggle-${position}`}
          style={{ color: toggleColor }}
        >
          <span className={`staggered-menu-burger${isOpen ? " is-open" : ""}`}>
            <span />
            <span />
            <span />
          </span>
          {toggleLabel && (
            <span className="staggered-menu-toggle-label">{toggleLabel}</span>
          )}
        </button>
      )}

      <div
        className={`staggered-menu-root staggered-menu-${position}${
          isOpen ? " is-open" : ""
        }`}
        aria-hidden={!isOpen}
      >
        <div
          className="staggered-menu-backdrop"
          onClick={() => setOpen(false)}
        />

        <div className="staggered-menu-layers" aria-hidden="true">
          {colors.map((c, i) => (
            <div
              key={i}
              className="staggered-menu-layer"
              style={{
                backgroundColor: c,
                transitionDelay: isOpen
                  ? `${i * 70}ms`
                  : `${(colors.length - i) * 40}ms`,
              }}
            />
          ))}
        </div>

        <div
          className="staggered-menu-panel"
          style={{
            transitionDelay: isOpen ? `${panelDelayMs}ms` : "0ms",
            // The panel was always a hardcoded solid white (see the file
            // header comment) — that's fine on its own, but it never
            // matched the rest of the app once dark/light mode existed.
            // These two vars are already defined app-wide by the parent
            // screen's themed root div (`data-theme="dark"/"light"`), so
            // they inherit correctly here with no extra wiring needed —
            // this just overrides the old fixed white/black.
            backgroundColor: "var(--bg-container)",
            color: "var(--text-primary)",
          }}
        >
          <button
            type="button"
            onClick={handleCloseButtonClick}
            aria-label="Close menu"
            className={`staggered-menu-close${
              isCloseButtonPressed ? " is-pressed" : ""
            }`}
            style={{ ["--sm-accent" as any]: accentColor }}
          >
            {closeLabel && (
              <span className="staggered-menu-close-label">{closeLabel}</span>
            )}
            <span className="staggered-menu-close-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path
                  d="M4 4L20 20M20 4L4 20"
                  stroke="currentColor"
                  strokeWidth="2.25"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </button>

          {logoUrl && (
            <img src={logoUrl} alt="" className="staggered-menu-logo" />
          )}

          {panelTitle && (
            <div
              className="staggered-menu-title"
              style={{
                transitionDelay: isOpen ? `${panelDelayMs + 40}ms` : "0ms",
                color: accentColor,
              }}
            >
              {panelTitle}
            </div>
          )}

          <nav className="staggered-menu-nav">
            <ul>
              {items.map((item, i) => (
                <li
                  key={item.label + i}
                  className={`staggered-menu-item${
                    item.isActive ? " is-active" : ""
                  }`}
                  style={{
                    transitionDelay: isOpen
                      ? `${panelDelayMs + 60 + i * 55}ms`
                      : `${(items.length - i) * 30}ms`,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleItemActivate(item)}
                    aria-label={item.ariaLabel || item.label}
                    className="staggered-menu-item-btn"
                    style={{ ["--sm-accent" as any]: accentColor }}
                  >
                    {displayItemNumbering && (
                      <span className="staggered-menu-index">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    )}
                    <span className="staggered-menu-label">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {displaySocials && socialItems.length > 0 && (
            <div
              className="staggered-menu-socials"
              style={{
                transitionDelay: isOpen
                  ? `${panelDelayMs + 60 + items.length * 55 + 60}ms`
                  : "0ms",
              }}
            >
              {socialItems.map((s, i) => (
                <button
                  key={s.label + i}
                  type="button"
                  onClick={() => handleItemActivate(s)}
                  className="staggered-menu-social-link"
                  style={{ ["--sm-accent" as any]: accentColor }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
